const BASE_URLS = {
  auth: '/auth',
  signIn: '/sign-in',
  signUp: '/sign-up',
  profile: '/profile',
  classes: '/classes',
  routines: '/routines',
  events: '/events',
  calendar: '/calendar',
  dancers: '/dancers',
  parents: '/parents',
  team: '/employees',
  payments: '/payment',
  media: '/media',
  notification: '/notification',
  settings: '/settings',
  performances: '/performances',
  studio: '/studio',
  invoices: '/invoices',
  dashboard: '/dashboard',
  notes: '/notes',
};

export const WEB_API_ROUTES = {
  general: {
    search: '/search',
    getStates: '/location/states',
    getCities: '/location/cities',
    getTimezones: '/data/timezones',
    students: 'data/students',
    users: 'studio/users',
    rooms: 'data/rooms',
    teachers: 'data/teachers',
    routineTeachers: 'routines/teachers',
    profile: '/profile',
    studio: '/studio/{studioId}',
    routineStatuses: 'data/statuses',
    routineTypes: 'data/types',
    routineLevels: 'data/levels',
    styles: 'data/styles',
    tags: '/data/tags',
    nicknames: '/data/nicknames',
    getTagsAndNicknames: '/search/nicknames-and-tags',
  },

  auth: {
    logIn: `${BASE_URLS.signIn}/login`,
    logOut: `${BASE_URLS.profile}/logout`,
    refreshToken: `${BASE_URLS.signIn}/refresh`,
    googleLogin: `${BASE_URLS.signIn}/with-google`,
    appleLogin: `${BASE_URLS.signIn}/with-apple`,
    forgotPassword: `${BASE_URLS.signIn}/forgot-password`,
    createPassword: `${BASE_URLS.signIn}/create-password`,
    validateSignUp: `${BASE_URLS.signUp}/validate`,
    googleSignUp: `${BASE_URLS.signUp}/with-google`,
    appleSignUp: `${BASE_URLS.signUp}/with-apple`,
  },

  registration: {
    signUp: BASE_URLS.signUp,
    verifyCode: `${BASE_URLS.signUp}/verify-code`,
    sendVerifyCode: `${BASE_URLS.signUp}/send-verify-code`,
  },

  schedule: {
    teachers: `${BASE_URLS.calendar}/teachers`,
    students: `${BASE_URLS.calendar}/students`,
    parents: `${BASE_URLS.calendar}/parents`,
    classes: `${BASE_URLS.calendar}/classes`,
    appointments: `${BASE_URLS.calendar}/appointments`,
    createAppointment: `${BASE_URLS.calendar}/appointments/store`,
    updateAppointment: `${BASE_URLS.calendar}/appointments/update`,
    deleteAppointment: `${BASE_URLS.calendar}/appointments/delete/{appointmentId}`,
    cancelAppointment: `${BASE_URLS.calendar}/appointments/cancel-and-notify/{appointmentId}`,
    updateDancerRating: `${BASE_URLS.calendar}/appointments/ratings/update`,
    updateAttendance: `${BASE_URLS.calendar}/appointments/ratings/set-dancer-attendance`,
    storeInternal: `${BASE_URLS.calendar}/appointments/store-internal`,
    updateInternal: `${BASE_URLS.calendar}/appointments/update-internal/{appointmentId}`,
    storeUnavailable: `${BASE_URLS.calendar}/appointments/store-unavailable`,
    updateUnavailable: `${BASE_URLS.calendar}/appointments/update-unavailable/{appointmentId}`,
    approveRequest: `${BASE_URLS.calendar}/appointments/requests/approve`,
    rejectRequest: `${BASE_URLS.calendar}/appointments/requests/reject`,

    sendSms: `${BASE_URLS.calendar}/notification/phone`,
    sendEmail: `${BASE_URLS.calendar}/notification/email`,
  },

  dancers: {
    list: BASE_URLS.dancers,
    update: `${BASE_URLS.dancers}/{dancerId}/update`,
    info: `${BASE_URLS.dancers}/{dancerId}`,
    evaluation: `${BASE_URLS.dancers}/{dancerId}/evaluation`,
    ratings: `${BASE_URLS.dancers}/{dancerId}/ratings`,
    performance: `${BASE_URLS.dancers}/{dancerId}/performances`,
    notifications: `${BASE_URLS.dancers}/{dancerId}/notifications`,
    email: `${BASE_URLS.dancers}/notification/email`,
    sms: `${BASE_URLS.dancers}/notification/phone`,
    routines: `${BASE_URLS.dancers}/{dancerId}/routines`,
    appointments: `${BASE_URLS.dancers}/{dancerId}/appointments`,
    uploadAvatar: `${BASE_URLS.dancers}/{dancerId}/upload-avatar`,
    removeAvatar: `${BASE_URLS.dancers}/{dancerId}/remove-avatar`,
    classes: `${BASE_URLS.dancers}/{dancerId}/classes`,
    createRelative: `${BASE_URLS.dancers}/relatives/store`,
    relatives: `${BASE_URLS.dancers}/relatives/list`,
    media: `${BASE_URLS.dancers}/{dancerId}/media`,
    importCsv: `${BASE_URLS.dancers}/import`,
  },

  classes: {
    list: BASE_URLS.classes,
    dancers: `${BASE_URLS.classes}/{classId}/dancers`,
    updateDancers: `${BASE_URLS.classes}/{classId}/dancers/join`,
    info: `${BASE_URLS.classes}/{classId}`,
    pricing: `${BASE_URLS.classes}/{classId}/pricing-options`,
    appointments: `${BASE_URLS.classes}/{classId}/upcoming-appointments`,
    styles: `${BASE_URLS.classes}/styles`,
    schedule: `${BASE_URLS.classes}/{classId}/schedule`,
    scheduleCheck: `${BASE_URLS.classes}/schedule/check`,
    toggleActive: `${BASE_URLS.classes}/{classId}/set-activation`,
    recipientsList: `${BASE_URLS.classes}/{classId}/notification/get-recipients-list`,
    email: `${BASE_URLS.classes}/{classId}${BASE_URLS.notification}/email`,
    sms: `${BASE_URLS.classes}/{classId}${BASE_URLS.notification}/phone`,
    media: `${BASE_URLS.classes}/{classId}/media`,
  },

  routines: {
    list: BASE_URLS.routines,
    routineInfo: `${BASE_URLS.routines}/{routineId}`,
    audio: `${BASE_URLS.routines}/{routineId}/audio`,
    dancers: `${BASE_URLS.routines}/{routineId}/dancers`,
    updateDancers: `${BASE_URLS.routines}/{routineId}${BASE_URLS.dancers}/join`,
    detachDancers: `${BASE_URLS.routines}/{routineId}${BASE_URLS.dancers}/detach`,
    performances: `${BASE_URLS.routines}/{routineId}/performances/list`,
    performancesInfo: `${BASE_URLS.routines}/{routineId}/performances`,
    media: `${BASE_URLS.routines}/{routineId}/media`,
  },

  events: {
    list: BASE_URLS.events,
    info: `${BASE_URLS.events}/{eventId}`,
    performances: `${BASE_URLS.events}/{eventId}/performances/list`,
    recipientsList: `${BASE_URLS.events}/{eventId}${BASE_URLS.notification}/get-recipients-list`,
    sendEmail: `${BASE_URLS.events}/{eventId}${BASE_URLS.notification}/email`,
    sendSms: `${BASE_URLS.events}/{eventId}${BASE_URLS.notification}/phone`,
    performancesInfo: `${BASE_URLS.events}/{eventId}/performances`,
  },

  notifications: {
    sendEmail: `${BASE_URLS.notification}/email`,
    sendSms: `${BASE_URLS.notification}/phone`,
    recipientsList: `${BASE_URLS.notification}/get-recipients-list`,
    reply: `${BASE_URLS.notification}/reply`,
  },

  payments: {
    checkout: `${BASE_URLS.payments}/stripe/checkout`,
    createIntent: `${BASE_URLS.payments}/stripe/create-intent`,
    addMethod: `${BASE_URLS.payments}/add-payment-method`,
    listTransactions: `${BASE_URLS.payments}/transactions`,
  },

  settings: {
    config: `${BASE_URLS.studio}${BASE_URLS.settings}`,
    deleteLocation: `${BASE_URLS.studio}${BASE_URLS.settings}/{locationId}`,
    deleteRoom: `${BASE_URLS.studio}${BASE_URLS.settings}/room/{roomId}`,
    uploadStudioAvatar: `${BASE_URLS.studio}/{studioId}/upload-avatar`,
    removeStudioImg: `${BASE_URLS.studio}/{studioId}/remove-avatar`,
  },

  performances: {
    info: `${BASE_URLS.performances}/{performanceId}`,
    delete: `${BASE_URLS.performances}/delete/{performanceId}`,
    routineDancers: `${BASE_URLS.performances}/dancers/list/{routineId}/{performanceId}`,
    createEventPerformance: `${BASE_URLS.events}/performances/{eventId}`,
    performance: `${BASE_URLS.performances}/{performanceId}`,
    performanceDancers: `${BASE_URLS.performances}/{performanceId}/dancers`,
    updatePerformanceDancers: `${BASE_URLS.performances}/{performanceId}/dancers/join`,
    dancersList: `${BASE_URLS.performances}/{performanceId}/dancers/{routineId}/list`,
  },

  parents: {
    list: BASE_URLS.parents,
    sendEmail: `${BASE_URLS.parents}/notification/email`,
    sendSms: `${BASE_URLS.parents}/notification/phone`,
    getRelatives: `${BASE_URLS.parents}/relatives/list`,
    createRelative: `${BASE_URLS.parents}/relatives/store`,
    parentInfo: `${BASE_URLS.parents}/{parentId}`,
    dancers: `${BASE_URLS.parents}/{parentId}/dancers`,
    notifications: `${BASE_URLS.parents}/{parentId}/notifications`,
    classes: `${BASE_URLS.parents}/{parentId}/classes`,
    parentClasses: `${BASE_URLS.parents}/{parentId}/classes`,
  },

  team: {
    list: BASE_URLS.team,
    info: `${BASE_URLS.team}/{employeesId}`,
    status: `${BASE_URLS.team}/{employeesId}/set-activation`,
    removeEmployeesAvatar: `${BASE_URLS.team}/{employeesId}/remove-avatar`,
  },

  media: {
    list: `${BASE_URLS.media}`,
    info: `${BASE_URLS.media}/{mediaId}`,
    visibilityRequest: `${BASE_URLS.studio}/visibility-request`,
  },

  invoices: {
    list: `${BASE_URLS.invoices}`,
    info: `${BASE_URLS.invoices}/{invoiceId}`,
    customerSearch: `search/customers`,
    itemsSearch: `search/items`,
    items: `${BASE_URLS.invoices}/{invoiceId}/line-items`,
    item: `${BASE_URLS.invoices}/{invoiceId}/line-items/{itemId}`,
    edit: `${BASE_URLS.invoices}/{invoiceId}/payment`,
    schedules: `${BASE_URLS.invoices}/{invoiceId}/payment-schedules`,
  },

  profile: {
    info: `${BASE_URLS.profile}/user`,
    connections: `${BASE_URLS.profile}/user/connections`,
  },

  dashboard: {
    list: `${BASE_URLS.dashboard}/dancers-per-day`,
  },

  notes: {
    info: `${BASE_URLS.notes}`,
    delete: `${BASE_URLS.notes}/{noteId}`,
  },
};
